'use client'

// The first line, as a card.
//
// Two lines a stranger can read in five seconds: what he is doing right
// now and why, with the person who set it off when there was one; and the
// most recent thing that changed in him or in what he does, again with the
// person when a visitor caused it. Both are composed on the server from
// facts (sim-server/src/world/Now.js), never from prose a model wrote.
//
// It replaced a card that preferred a catalog story's question, which read
// "What the pond does to him" for eight days straight because a place he
// visits daily can never resolve. A card that reads the same for a week is
// the opposite of a live mind, and a live mind is the whole product.
//
// The now line rides the NPC delta, so it changes the second he does. The
// change line comes from /api/life on a slow poll; it moves at the pace of
// his day, not his feet.
//
// It can be folded away (29 Sep, Sam on a phone: "this box has no way to
// close, and it obscures the view badly"). Folded, it is a small pill that
// opens it again, and the choice is remembered on this device. On a phone
// the pill sits under the clock, in band 3 left of the minimap (see
// lib/ui/layout.js); the open card keeps band 4.

import { useCallback, useEffect, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { kawaii, stitchInset, ui } from '../../lib/ui/theme'
import { CloseButton } from './KawaiiButton'

const POLL_MS = 45_000
const CLOCK_MS = 30_000
const FOLD_KEY = 'pino.lifeNow.folded'

function cap(s) {
  const t = String(s || '')
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t
}

function agoLabel(ts, now) {
  const ms = now - (ts || 0)
  if (!(ms > 0)) return 'just now'
  const min = Math.round(ms / 60_000)
  if (min < 2) return 'just now'
  if (min < 60) return `${min} min ago`
  const h = Math.round(ms / 3_600_000)
  if (h < 24) return `${h}h ago`
  return h < 48 ? 'yesterday' : `${Math.round(h / 24)} days ago`
}

export function LifeNow() {
  const now = useGameStore((state) => state.npcStates?.[0]?.now || null)
  const life = useGameStore((state) => state.lifeState)
  const setLife = useGameStore((state) => state.setLifeState)
  const setJournalOpen = useGameStore((state) => state.setJournalOpen)
  const setJournalTab = useGameStore((state) => state.setJournalTab)
  // On a phone the menu fans out over this card and its labels landed on
  // the text (measured 23 Sep: "What's on" sat on the changed-lately line).
  // The world dims under the menu; the card gets out of the way the same way.
  const menuOpen = useGameStore((state) => state.menuOpen)
  const signature = useRef('')
  // "12 min ago" has to move without a new fetch, or the card lies a little
  // more every minute it sits open.
  const [clock, setClock] = useState(() => Date.now())
  // Read after mount so the server render and the first client render agree.
  const [folded, setFolded] = useState(false)
  useEffect(() => {
    try { setFolded(localStorage.getItem(FOLD_KEY) === '1') } catch { /* no storage, stays open */ }
  }, [])
  const fold = (value) => {
    setFolded(value)
    try { localStorage.setItem(FOLD_KEY, value ? '1' : '0') } catch { /* this visit only */ }
  }

  const readLife = useCallback(async () => {
    try {
      const response = await fetch('/api/life?npc=victor', { cache: 'no-store' })
      if (!response.ok) return
      const next = await response.json()
      const sig = JSON.stringify([
        next.updatedAt,
        next.current,
        next.stories?.map((story) => [story.id, story.updatedAt]),
      ])
      if (sig === signature.current) return
      signature.current = sig
      setLife(next)
    } catch {
      // The world can keep running while the read model reconnects.
    }
  }, [setLife])

  useEffect(() => {
    readLife()
    const timer = setInterval(readLife, POLL_MS)
    return () => clearInterval(timer)
  }, [readLife])

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), CLOCK_MS)
    return () => clearInterval(timer)
  }, [])

  const change = life?.current?.latestChange || null
  if (!now?.doing && !change) return null

  const openDay = () => {
    setJournalTab('diary')
    setJournalOpen(true)
  }

  const eyebrow = now?.person ? `right now, because of ${now.person}` : 'right now'

  const away = menuOpen ? 'none' : 'auto'
  const dot = <span style={{ width: 7, height: 7, borderRadius: 999, background: '#7c5cbf', flex: 'none' }} />

  return (
    <div
      className={`fixed z-40 right-3 ${folded ? 'top-[124px] left-3' : 'top-[224px] left-3'} md:left-6 md:right-auto md:top-[88px] md:w-[310px] pointer-events-none`}
      style={{ opacity: menuOpen ? 0 : 1, transition: 'opacity 200ms ease' }}
    >
      {folded ? (
        <button
          type="button"
          onClick={() => fold(false)}
          aria-label="Show what he is doing"
          tabIndex={menuOpen ? -1 : 0}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            background: kawaii.cream,
            border: kawaii.outlineThin,
            borderRadius: kawaii.radiusPill,
            boxShadow: kawaii.dropSm,
            padding: '6px 12px',
            fontFamily: ui.font,
            color: kawaii.ink,
            fontSize: 12,
            fontWeight: 800,
            pointerEvents: away,
          }}
        >
          {dot}
          right now
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={openDay}
            className="block w-full text-left"
            aria-label="Read his diary"
            tabIndex={menuOpen ? -1 : 0}
            style={{
              position: 'relative',
              background: kawaii.cream,
              border: kawaii.outline,
              borderRadius: 18,
              boxShadow: kawaii.drop,
              padding: '11px 15px 12px',
              fontFamily: ui.font,
              pointerEvents: away,
            }}
          >
            <span style={stitchInset(18)} />

            {now?.doing && (
              <>
                <span style={{ display: 'flex', alignItems: 'center', gap: 7, paddingRight: 30, color: kawaii.inkSoft, fontSize: 12, fontWeight: 800 }}>
                  {dot}
                  {eyebrow}
                </span>
                <span style={{ display: 'block', marginTop: 4, color: kawaii.ink, fontSize: 14.5, fontWeight: 850, lineHeight: 1.25 }}>
                  {cap(now.doing)}
                </span>
                {now.why && (
                  <span style={{ display: 'block', marginTop: 3, color: kawaii.inkSoft, fontSize: 13, fontWeight: 650, lineHeight: 1.4 }}>
                    {cap(now.why)}
                  </span>
                )}
              </>
            )}

            {change && (
              <span style={{ display: 'block', marginTop: now?.doing ? 9 : 0, paddingTop: now?.doing ? 8 : 0, borderTop: now?.doing ? kawaii.stitch : 'none' }}>
                <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, paddingRight: now?.doing ? 0 : 30, color: kawaii.inkSoft, fontSize: 12, fontWeight: 800 }}>
                  <span>{change.person ? `changed lately, ${change.person}` : 'changed lately'}</span>
                  <span style={{ fontWeight: 650, flex: 'none' }}>{agoLabel(change.at, clock)}</span>
                </span>
                <span style={{ display: 'block', marginTop: 2, color: kawaii.ink, fontSize: 12.5, fontWeight: 650, lineHeight: 1.4 }}>
                  {change.text}
                </span>
              </span>
            )}

            <span style={{ display: 'block', marginTop: 7, color: '#5f42ab', fontSize: 12, fontWeight: 800 }}>
              read his diary
            </span>
          </button>
          <span style={{ position: 'absolute', top: 8, right: 8, pointerEvents: away }}>
            <CloseButton onClick={() => fold(true)} tabIndex={menuOpen ? -1 : 0} />
          </span>
        </>
      )}
    </div>
  )
}

export default LifeNow
